import React, { useState, useRef, useEffect } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import JSZip from 'jszip';
import { AuditLoggers } from '../../../utils/auditLogger';
import generateChargebackPdf from '../../../utils/generatePdf';
import { formatDate, formatAmount, formatDateTime } from '../../../utils/global';
import { CHARGEBACK_STAGE, classifyChargebackStage } from '../../../utils/chargebackStages';
import {
  DEFAULT_VERTICAL,
  emptyReasonCodeResolved,
  formatCategoryLabel,
  formatDueDateDisplay,
  formatProvisionalWinLabel,
  isAcceptLiabilityPosture,
  isComplianceCategory,
  isNonActionablePosture,
  isNotADisputePosture,
  isRefundProofOnly,
  shouldShowDocumentChecklist,
} from '../../../utils/chargebackReasonCode';
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Chip,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  Tooltip,
} from '@mui/material';
import MuiLink from '@mui/material/Link';
import {
  ArrowBack as ArrowBackIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Download as DownloadIcon,
  NoteAdd as NoteAddIcon,
  Upload as UploadIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  DeleteOutline as DeleteOutlineIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { extractS3ObjectKeyFromUrl } from '../../../utils/chargebackEvidence';
import {
  anySelectedFileNeedsManualNote,
  buildUploadAnalyzeLetterHtml,
  getFulfilledSuggestionKeys,
  isChargebackFileParseable,
  isConfirmedUnmaskedPan,
  PAN_UPLOAD_WARNING,
  parseSuggestedDocumentsSnapshot,
  parseUploadAnalyzeMeta,
  uploadAcceptedPanRisk,
} from '../../../utils/chargebackAnalyze';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonateCardSx,
  harmonateProceedButtonSx,
  harmonateUploadButtonSx,
  harmonateUploadOutlinedSx,
  harmonateCancelButtonSx,
  harmonateBlueButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateTextFieldSx,
  harmonateInputLabelProps,
  harmonateSelectMenuProps,
  harmonateSectionTitleSx,
  harmonateLinkSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const SUGGESTED_DOCS_SNAPSHOT_ACTION = 'SUGGESTED_DOCUMENTS_SNAPSHOT';
const HIDDEN_HISTORY_ACTIONS = new Set([
  SUGGESTED_DOCS_SNAPSHOT_ACTION,
  'ANALYSIS_FINISHED',
  'CONVERSION_FINISHED', // Tommy HAR-1507: spinner only; do not list convert rows in history
  'CONVERSION_FAILED',
]);

const isVisibleHistoryAction = (action) =>
  !HIDDEN_HISTORY_ACTIONS.has(String(action || '').toUpperCase());

/** True when history MESSAGE is a real note/summary (not the old generic upload placeholder). */
const isMeaningfulHistorySummary = (message) => {
  const text = String(message || '').trim();
  if (!text) return false;
  if (/^document uploaded\.?$/i.test(text)) return false;
  return true;
};

const getHistorySummaryDisplayText = (message) => {
  if (isMeaningfulHistorySummary(message)) return String(message).trim();
  return 'No summary available';
};

const sortSuggestedDocEntries = (map = {}) =>
  Object.entries(map || {}).sort(([a], [b]) => {
    const aNum = /^\d+$/.test(a);
    const bNum = /^\d+$/.test(b);
    if (aNum && bNum) return Number(a) - Number(b);
    if (aNum) return -1;
    if (bNum) return 1;
    return String(a).localeCompare(String(b));
  });


// Function to format status for consistent display
const formatStatus = (status) => {
  if (!status) return 'N/A';
  // Convert to title case for display
  return status.toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/** Snowflake / API expect STATUS like PENDING — not display text from history chips ("Pending"). */
const normalizeHistoryStatusForDb = (status) => {
  if (status == null || status === '') return 'PENDING';
  return String(status).trim().toUpperCase().replace(/\s+/g, '_');
};

const postChargebackHistory = (tenantIntegration, token, body) =>
  fetch(
    `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/chargebacks/${tenantIntegration}/history/`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-Tenant-ID': tenantIntegration,
      },
      body: JSON.stringify(body),
    }
  );

const deleteStoredFile = async (token, s3Key) => {
  if (!s3Key) return;
  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  const response = await fetch(
    `${baseUrl}conductor/files?fileKey=${encodeURIComponent(s3Key)}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to delete file');
  }
};

const documentUploadedHistoryBody = ({
  chargebackFk,
  statusValue,
  uploadNote,
  uploadItemType,
  suggestedSnapshot,
  file,
  result,
  analyze,
  panRiskAccepted = false,
}) => {
  const summaryOrNote =
    (analyze?.parseable && analyze?.summary) ||
    String(uploadNote || '').trim() ||
    null;
  const matchedKey = analyze?.matched_suggestion_key || uploadItemType || null;
  const extras = {
    suggested_label: matchedKey ? suggestedSnapshot?.[matchedKey] || null : null,
  };
  if (panRiskAccepted) extras.pan_risk_accepted = true;
  return {
    chargeback_id_fk: chargebackFk,
    action: 'DOCUMENT_UPLOADED',
    message: summaryOrNote,
    letter_html: buildUploadAnalyzeLetterHtml(analyze || {}, extras),
    file_location: result?.fileUrl || '',
    file_name: result?.fileName || file?.name || '',
    item_type: matchedKey,
    status: statusValue,
    created_by: localStorage.getItem('username') || 'system',
  };
};

const emptyAnalyzeResult = (uploadItemType) => ({
  parseable: false,
  document_type: null,
  summary: null,
  matched_suggestion_key: uploadItemType || null,
  match_rationale: null,
  contains_unmasked_pan: false,
  pan_detection_method: null,
  pan_finding_count: null,
});

const CaseDetails = () => {
  const { canAdd, canEdit, canUpload, canExport } = useFunctionPermissions(FC.MERCHANT_DATA_HUB);
  const navigate = useNavigate();
  const { caseId } = useParams();
  const location = useLocation();
  const [showUploadDialog, setShowUploadDialog] = React.useState(false);
  const [historyDetailsDialog, setHistoryDetailsDialog] = useState({
    open: false,
    title: '',
    message: '',
  });
  
  // State for dialogs and forms
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [note, setNote] = useState('');
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [isFightTransactionCompleted, setIsFightTransactionCompleted] = useState(() => {
    // Check localStorage for existing fight transaction completion status for this case
    const savedStatus = localStorage.getItem(`fightCompleted_${caseId}`);
    return savedStatus === 'true';
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [uploadNote, setUploadNote] = useState('');
  // Snapshot of suggested docs as { "1": "label", ... } — passed to /analyze.
  const [suggestedDocumentsMap, setSuggestedDocumentsMap] = useState({});
  const [loadingSuggestedDocuments, setLoadingSuggestedDocuments] = useState(false);
  const [reasonCodeResolved, setReasonCodeResolved] = useState(emptyReasonCodeResolved);
  const [loadingReasonCodeResolved, setLoadingReasonCodeResolved] = useState(false);
  const [analyzingUpload, setAnalyzingUpload] = useState(false);
  const [convertingDocuments, setConvertingDocuments] = useState(false);
  const [convertingCount, setConvertingCount] = useState(0);
  const [panReview, setPanReview] = useState(null);
  const [panReviewBusy, setPanReviewBusy] = useState(false);
  const [fileToRemove, setFileToRemove] = useState(null);
  const [removingStoredFile, setRemovingStoredFile] = useState(false);
  // Optional manual match key when file is non-parseable / AI cannot match.
  const [uploadItemType, setUploadItemType] = useState('');
  const needsManualUploadNote = anySelectedFileNeedsManualNote(selectedFiles);
  
  // Toast notification state
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'success' // 'success', 'error', 'warning', 'info'
  });
  
  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };
  
  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToast(prev => ({ ...prev, open: false }));
  };
  const [canGeneratePdf, setCanGeneratePdf] = useState(false);
  
  // Get the case data from the route state
  // Decode the caseId from the URL first
  const decodedCaseId = caseId ? decodeURIComponent(caseId) : '';
  
  const initialCaseData = location.state?.caseData || {
    ID: decodedCaseId,
    CASE_NO: decodedCaseId,
    history: [
      { 
        date: new Date().toISOString().split('T')[0], 
        status: 'Case Opened', 
        description: 'Case was created from chargeback list' 
      }
    ]
  };
  
  const [caseData, setCaseData] = useState(initialCaseData);
  // Initialize history as an empty array to ensure it's always an array
  const [history, setHistory] = useState(Array.isArray(initialCaseData.history) ? initialCaseData.history : []);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);

  // Fetch case history from API
  const fetchCaseHistory = async () => {
    if (!decodedCaseId) return;
    
    setLoadingHistory(true);
    setHistoryError(null);
    
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const tenantIntegrationId = localStorage.getItem('tenant_integration');
      
      if (!token || !tenantIntegrationId) {
        throw new Error('Authentication token or tenant integration ID not found');
      }
      
      // Use the full chargeback ID as a string
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/chargebacks/${tenantIntegrationId}/${encodeURIComponent(decodedCaseId)}/history/`;
      console.log('Fetching history from:', apiUrl);
      
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-ID': tenantIntegrationId
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch case history: ${response.statusText}`);
      }
      
      const responseData = await response.json();
      // Extract the data array from the response
      const historyData = responseData.data || [];
      
      // Update case status if we have history items
      if (historyData.length > 0) {
        const latestStatus = historyData[0]; // Assuming the first item is the latest
        setCaseData(prev => ({
          ...prev,
          status: latestStatus.STATUS.replace('_', ' '),
          lastUpdated: latestStatus.DATE_MODIFIED,
          updatedBy: latestStatus.CREATED_BY
        }));
      }
      
      // Pair each upload with the next conversion so downloads use the converted
      // PDF/TIF after convert finishes (Tommy HAR-1507), even though convert
      // rows stay hidden from the history table.
      const oldestFirst = [...historyData].reverse();
      const uploadIdToConverted = new Map();
      const pendingUploadIds = [];
      oldestFirst.forEach((item) => {
        const action = String(item?.ACTION || '').toUpperCase();
        if (action === 'DOCUMENT_UPLOADED' && item?.FILE_LOCATION) {
          pendingUploadIds.push(item.ID);
        } else if (
          action === 'CONVERSION_FINISHED' &&
          item?.FILE_LOCATION &&
          pendingUploadIds.length > 0
        ) {
          const uploadId = pendingUploadIds.shift();
          uploadIdToConverted.set(uploadId, {
            FILE_LOCATION: item.FILE_LOCATION,
            FILE_NAME: item.FILE_NAME || item.FILE_LOCATION.split('/').pop(),
          });
        }
      });

      // Map API response to history items
      const formattedHistory = historyData.map(item => {
        const action = String(item?.ACTION || '').toUpperCase();
        const converted = action === 'DOCUMENT_UPLOADED'
          ? uploadIdToConverted.get(item.ID)
          : null;
        const fileLocation = converted?.FILE_LOCATION || item.FILE_LOCATION || null;
        const fileName = converted?.FILE_NAME || item.FILE_NAME || null;
        return {
        id: item.ID,
        // Include time-of-day so rows from the same calendar day stay readable in chronological order.
        date: item.DATE_MODIFIED ? formatDateTime(new Date(item.DATE_MODIFIED)) : '',
        itemType: item.ITEM_TYPE || null,
        letterHtml: item.LETTER_HTML || null,
        analyzeMeta: parseUploadAnalyzeMeta(item.LETTER_HTML),
        panRiskAccepted: uploadAcceptedPanRisk(item.LETTER_HTML),
        rawStatus: item.STATUS,
        status: item.STATUS.replace('_', ' '),
        action: item.ACTION,
        message: item.MESSAGE,
        fileLocation,
        FILE_LOCATION: fileLocation,
        FILE_NAME: fileName,
        file: fileLocation
          ? {
              name: fileName || 'Document',
              url: fileLocation
            }
          : null,
        user: item.CREATED_BY
      };
      });
      
      setHistory(formattedHistory);
      return formattedHistory;
    } catch (error) {
      console.error('Error fetching case history:', error);
      setHistoryError(error.message);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Fetch history when component mounts or case ID changes
  useEffect(() => {
    if (caseData.ID) {
      fetchCaseHistory();
    }
  }, [caseData.ID]);

  // Load resolved reason-code row + suggested documents for this case.
  // Prefer an existing SUGGESTED_DOCUMENTS_SNAPSHOT history row so keys stay stable;
  // otherwise fetch from CHARGEBACK_REASON_CODE_RESOLVED and persist a snapshot once.
  const suggestedSnapshotWriteRef = React.useRef(false);

  useEffect(() => {
    suggestedSnapshotWriteRef.current = false;
    setSuggestedDocumentsMap({});
    setReasonCodeResolved(emptyReasonCodeResolved());
  }, [caseData.ID, caseData.REASON_CODE_KEY]);

  useEffect(() => {
    let cancelled = false;

    const persistSuggestedDocumentsSnapshot = async (docsMap, statusValue) => {
      if (suggestedSnapshotWriteRef.current) return;
      suggestedSnapshotWriteRef.current = true;

      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const tenantIntegrationId = localStorage.getItem('tenant_integration');
      const chargebackFk = String(
        decodedCaseId || caseData.ID || caseData.id || caseData.caseNumber || ''
      );
      if (!token || !tenantIntegrationId || !chargebackFk || !Object.keys(docsMap || {}).length) {
        suggestedSnapshotWriteRef.current = false;
        return;
      }

      try {
        await fetch(
          `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/chargebacks/${tenantIntegrationId}/history/`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              'X-Tenant-ID': tenantIntegrationId,
            },
            body: JSON.stringify({
              chargeback_id_fk: chargebackFk,
              action: SUGGESTED_DOCS_SNAPSHOT_ACTION,
              message: 'Suggested documents snapshot',
              letter_html: JSON.stringify(docsMap),
              status: statusValue || 'PENDING',
              created_by: localStorage.getItem('username') || 'system',
            }),
          }
        );
      } catch (e) {
        console.warn('Failed to persist suggested documents snapshot:', e);
        suggestedSnapshotWriteRef.current = false;
      }
    };

    const loadReasonCodeAndDocuments = async () => {
      const reasonCodeKey = String(caseData.REASON_CODE_KEY ?? '').trim();
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const tenantIntegrationId = localStorage.getItem('tenant_integration');
      if (!token || !tenantIntegrationId) return;

      // Prefer snapshot already stored on the case history for stable checklist keys.
      const snapshotRow = (history || []).find(
        (h) => String(h?.action || '').toUpperCase() === SUGGESTED_DOCS_SNAPSHOT_ACTION
      );
      const fromSnapshot = parseSuggestedDocumentsSnapshot(snapshotRow?.letterHtml);
      if (fromSnapshot && Object.keys(fromSnapshot).length) {
        if (!cancelled) {
          setSuggestedDocumentsMap(fromSnapshot);
          setLoadingSuggestedDocuments(false);
        }
      }

      if (!reasonCodeKey) {
        if (!cancelled) {
          setReasonCodeResolved(emptyReasonCodeResolved());
          setLoadingReasonCodeResolved(false);
          if (!(fromSnapshot && Object.keys(fromSnapshot).length)) {
            setSuggestedDocumentsMap({});
            setLoadingSuggestedDocuments(false);
          }
        }
        return;
      }

      setLoadingReasonCodeResolved(true);
      if (!(fromSnapshot && Object.keys(fromSnapshot).length)) {
        setLoadingSuggestedDocuments(true);
      }

      try {
        const params = new URLSearchParams({
          reason_code_key: reasonCodeKey,
          vertical: DEFAULT_VERTICAL,
        });
        const apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/chargebacks/${tenantIntegrationId}/reason-code-resolved/?${params.toString()}`;
        const resp = await fetch(apiUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-Tenant-ID': tenantIntegrationId,
          },
        });
        if (!resp.ok) {
          if (!cancelled) {
            setReasonCodeResolved(emptyReasonCodeResolved());
            if (!(fromSnapshot && Object.keys(fromSnapshot).length)) {
              setSuggestedDocumentsMap({});
            }
          }
          return;
        }
        const data = await resp.json();
        if (cancelled) return;

        setReasonCodeResolved({
          ...emptyReasonCodeResolved(),
          ...data,
          found: Boolean(data?.found),
          suggested_documents:
            data?.suggested_documents && typeof data.suggested_documents === 'object'
              ? data.suggested_documents
              : {},
        });

        // Only seed the checklist from resolved when no history snapshot exists.
        if (!(fromSnapshot && Object.keys(fromSnapshot).length)) {
          const docsMap =
            data?.suggested_documents && typeof data.suggested_documents === 'object'
              ? Object.fromEntries(
                  Object.entries(data.suggested_documents)
                    .filter(([, v]) => v != null && String(v).trim())
                    .map(([k, v]) => [String(k), String(v).trim()])
                )
              : {};
          setSuggestedDocumentsMap(docsMap);
          if (Object.keys(docsMap).length) {
            const statusSource =
              caseData.CHARGEBACK_STATUS ||
              caseData.Status ||
              (history && history.length > 0 ? history[0].rawStatus : null) ||
              'PENDING';
            await persistSuggestedDocumentsSnapshot(
              docsMap,
              normalizeHistoryStatusForDb(statusSource)
            );
          }
        }
      } catch (e) {
        console.warn('Failed to load reason-code resolved / suggested documents:', e);
        if (!cancelled) {
          setReasonCodeResolved(emptyReasonCodeResolved());
          if (!(fromSnapshot && Object.keys(fromSnapshot).length)) {
            setSuggestedDocumentsMap({});
          }
        }
      } finally {
        if (!cancelled) {
          setLoadingReasonCodeResolved(false);
          setLoadingSuggestedDocuments(false);
        }
      }
    };

    loadReasonCodeAndDocuments();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseData.ID, caseData.REASON_CODE_KEY, history]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleFileDownload = async (fileKey, fileName) => {
    if (!canExport) return;
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const tenantIntegration = localStorage.getItem('tenant_integration');
      const userEmail = localStorage.getItem('username') || 'unknown';
      
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Extract just the key part from S3 URL if a full URL is provided
      let actualFileKey = fileKey;
      try {
        const url = new URL(fileKey);
        // Remove the leading slash from pathname
        actualFileKey = url.pathname.startsWith('/') ? url.pathname.substring(1) : url.pathname;
      } catch (e) {
        // If it's not a valid URL, use it as is
        console.log('Using provided key as is (not a URL)');
      }
      
      // Log the file download attempt
      try {
        await AuditLoggers.chargebackDownloadDocument(
          caseData?.CASE_NO || 'N/A',
          caseId,
          actualFileKey,
          fileName || 'unknown',
          {
            userId: userEmail,
            tenantIntegration,
            caseStatus: caseData?.status || 'UNKNOWN',
            downloadTimestamp: new Date().toISOString(),
            fileSize: 0 // We don't know the size until after download
          }
        );
      } catch (logError) {
        console.error('Failed to log document download:', logError);
        // Don't fail the operation if logging fails
      }

      // Call the download endpoint
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/files/download?fileKey=${encodeURIComponent(actualFileKey)}${fileName ? `&fileName=${encodeURIComponent(fileName)}` : ''}`, 
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Failed to download file: ${response.statusText} - ${errorData}`);
      }

      // Get the content disposition header to get the filename
      const contentDisposition = response.headers.get('content-disposition');
      let downloadFileName = fileName || 'download';
      
      // Extract filename from content disposition if available
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          downloadFileName = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Get the content type from the response
      const contentType = response.headers.get('content-type') || 'application/octet-stream';
      
      // Convert the response to a blob
      const blob = await response.blob();
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([blob], { type: contentType }));
      
      // Create a temporary anchor element to trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadFileName);
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading file:', error);
      showToast(error.message || 'Failed to download file', 'error');
    }
  };

  const handleDownloadAllFiles = async () => {
    if (!canExport) return;
    // Filter history items that have files
    const itemsWithFiles = history.filter(item => item.file && item.file.url);
    
    if (itemsWithFiles.length === 0) {
      showToast('No files available to download', 'info');
      return;
    }

    setDownloadingAll(true);
    
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const tenantIntegration = localStorage.getItem('tenant_integration');
      const userEmail = localStorage.getItem('username') || 'unknown';
      
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const zip = new JSZip();
      let downloadCount = 0;
      let errorCount = 0;

      // Download each file and add to zip
      for (const item of itemsWithFiles) {
        try {
          // Extract file key from URL
          let actualFileKey = item.file.url;
          try {
            const url = new URL(item.file.url);
            actualFileKey = url.pathname.startsWith('/') ? url.pathname.substring(1) : url.pathname;
          } catch (e) {
            // If it's not a valid URL, use it as is
            console.log('Using provided key as is (not a URL)');
          }

          // Download the file
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}conductor/files/download?fileKey=${encodeURIComponent(actualFileKey)}${item.file.name ? `&fileName=${encodeURIComponent(item.file.name)}` : ''}`,
            {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );

          if (!response.ok) {
            console.error(`Failed to download file: ${item.file.name}`);
            errorCount++;
            continue;
          }

          // Get file blob
          const blob = await response.blob();
          
          // Add to zip with original filename
          zip.file(item.file.name || `file_${downloadCount + 1}`, blob);
          downloadCount++;
        } catch (error) {
          console.error(`Error downloading file ${item.file.name}:`, error);
          errorCount++;
        }
      }

      if (downloadCount === 0) {
        throw new Error('Failed to download any files');
      }

      // Generate zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Create download link
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      const zipFileName = `chargeback_evidence_${caseData.CASE_NO || decodedCaseId}_${new Date().toISOString().split('T')[0]}.zip`;
      link.setAttribute('download', zipFileName);
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Show success message
      if (errorCount > 0) {
        showToast(`Downloaded ${downloadCount} file(s). ${errorCount} file(s) failed to download.`, 'warning');
      } else {
        showToast(`Successfully downloaded ${downloadCount} file(s) as ZIP`, 'success');
      }

      // Log the download action
      try {
        await AuditLoggers.chargebackDownloadDocument(
          caseData?.CASE_NO || 'N/A',
          decodedCaseId,
          'all_files',
          zipFileName,
          {
            userId: userEmail,
            tenantIntegration,
            caseStatus: caseData?.status || 'UNKNOWN',
            downloadTimestamp: new Date().toISOString(),
            fileCount: downloadCount
          }
        );
      } catch (logError) {
        console.error('Failed to log download all action:', logError);
      }

    } catch (error) {
      console.error('Error downloading all files:', error);
      showToast(error.message || 'Failed to download all files', 'error');
    } finally {
      setDownloadingAll(false);
    }
  };

  const addFiles = (incomingFiles) => {
    const files = Array.from(incomingFiles || []).filter(Boolean);
    if (!files.length) return;
    setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
  };

  const handleChooseFile = () => {
    if (analyzingUpload) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    // Copy before clearing — FileList is live and emptying value clears it.
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    addFiles(files);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const convertClearDocuments = async ({
    clearItems,
    chargebackFk,
    statusValue,
    tenantIntegration,
    token,
  }) => {
    const convertible = (clearItems || []).filter((item) => item?.s3Key);
    if (!convertible.length) return;

    setConvertingDocuments(true);
    setConvertingCount(convertible.length);
    const processor = String(
      caseData.PROCESSOR || caseData.processor || caseData.CARD_BRAND || ''
    ).trim();
    const network = String(caseData.CARD_BRAND || caseData.NETWORK || '').trim() || undefined;
    const reasonCode = String(caseData.REASON_CODE ?? '').trim() || undefined;
    // Chase convert (chase-sftp) requires division/pde/last4 on processor_ref.
    const cardDigits = String(caseData.CARDHOLDER_NO || caseData.CARD_NUMBER || '').replace(/\D/g, '');
    const convertLast4 = cardDigits.slice(-4);
    let convertPde = '';
    const attrs = caseData.PROCESSOR_ATTRIBUTES;
    if (attrs && typeof attrs === 'object') {
      convertPde = String(attrs.sequence_number || attrs.SEQUENCE_NUMBER || '');
    } else if (typeof attrs === 'string' && attrs.trim()) {
      try {
        const parsed = JSON.parse(attrs);
        convertPde = String(parsed?.sequence_number || parsed?.SEQUENCE_NUMBER || '');
      } catch (_e) {
        convertPde = '';
      }
    }
    const convertDivision = String(
      caseData.DIVISION_NUMBER || caseData.DIVISION || ''
    ).replace(/\D/g, '');

    let converted = 0;
    let skipped = 0;
    let failed = 0;

    try {
      for (const item of convertible) {
        const sourceName = item.result?.fileName || item.file?.name || 'document';
        if (!processor) {
          skipped += 1;
          continue;
        }
        try {
          const convertResp = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/chargebacks/${tenantIntegration}/${encodeURIComponent(chargebackFk)}/convert/`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
                'X-Tenant-ID': tenantIntegration,
              },
              body: JSON.stringify({
                s3_key: item.s3Key,
                processor,
                network,
                reason_code: reasonCode,
                ...(convertLast4.length === 4 ? { last4: convertLast4 } : {}),
                ...(convertPde ? { pde: convertPde } : {}),
                ...(convertDivision ? { division: convertDivision } : {}),
              }),
            }
          );
          const convertData = await convertResp.json().catch(() => ({}));

          // Service not configured in this environment — skip quietly.
          if (convertResp.status === 503) {
            skipped += 1;
            continue;
          }

          if (!convertResp.ok) {
            failed += 1;
            const detail =
              typeof convertData?.detail === 'string'
                ? convertData.detail
                : `Could not convert ${sourceName}.`;
            await postChargebackHistory(tenantIntegration, token, {
              chargeback_id_fk: chargebackFk,
              action: 'CONVERSION_FAILED',
              message: detail,
              letter_html: null,
              file_location: '',
              file_name: sourceName,
              item_type: null,
              status: statusValue,
              created_by: localStorage.getItem('username') || 'system',
            });
            continue;
          }

          converted += 1;
          await postChargebackHistory(tenantIntegration, token, {
            chargeback_id_fk: chargebackFk,
            action: 'CONVERSION_FINISHED',
            message: `Converted PDF ready for ${sourceName}.`,
            letter_html: null,
            file_location: convertData.file_key || convertData.converted_ref || '',
            file_name: convertData.file_name || `${sourceName}.pdf`,
            item_type: null,
            status: statusValue,
            created_by: localStorage.getItem('username') || 'system',
          });
        } catch (convertError) {
          console.error('Convert request failed:', convertError);
          failed += 1;
          await postChargebackHistory(tenantIntegration, token, {
            chargeback_id_fk: chargebackFk,
            action: 'CONVERSION_FAILED',
            message: convertError.message || `Could not convert ${sourceName}.`,
            letter_html: null,
            file_location: '',
            file_name: sourceName,
            item_type: null,
            status: statusValue,
            created_by: localStorage.getItem('username') || 'system',
          });
        }
      }

      if (converted > 0 || failed > 0) {
        await fetchCaseHistory();
      }
      if (converted > 0 && failed === 0) {
        showToast(
          converted === 1
            ? 'Converted PDF is ready in case history.'
            : `${converted} converted PDFs are ready in case history.`,
          'success'
        );
      } else if (converted > 0 && failed > 0) {
        showToast(
          `${converted} converted. ${failed} could not be converted.`,
          'warning'
        );
      } else if (failed > 0 && skipped === 0) {
        showToast('Document conversion failed. Check case history for details.', 'error');
      }
    } finally {
      setConvertingDocuments(false);
      setConvertingCount(0);
    }
  };

  const handleFileUpload = async () => {
    if (!canUpload) return;
    if (selectedFiles.length === 0) return;

    if (needsManualUploadNote && !String(uploadNote || '').trim()) {
      showToast(
        'Please add a note describing this file and how it relates to the chargeback. Video, audio, archives, and files over 5 MB cannot be auto-analyzed.',
        'warning'
      );
      return;
    }

    const tenantId = localStorage.getItem('tenant_id');
    const tenantIntegration = localStorage.getItem('tenant_integration');
    
    if (!tenantId) {
      alert('Tenant information not found. Please log in again.');
      return;
    }

    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    const chargebackFk = String(
      decodedCaseId || caseData.ID || caseData.id || caseData.caseNumber || ''
    );
    const statusSource =
      caseData.CHARGEBACK_STATUS ||
      caseData.Status ||
      (history && history.length > 0 ? history[0].rawStatus : null) ||
      'PENDING';
    const statusValue = normalizeHistoryStatusForDb(statusSource);
    const suggestedSnapshot = { ...suggestedDocumentsMap };

    const analyzeUploadedFile = async (file, uploadResult) => {
      const parseableClient = isChargebackFileParseable(file);
      if (!parseableClient || !Object.keys(suggestedSnapshot).length) {
        return emptyAnalyzeResult(uploadItemType);
      }

      const s3Key =
        uploadResult?.s3Key ||
        extractS3ObjectKeyFromUrl(uploadResult?.fileUrl || '') ||
        '';
      if (!s3Key) {
        return emptyAnalyzeResult(uploadItemType);
      }

      try {
        const analyzeResp = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/chargebacks/${tenantIntegration}/${encodeURIComponent(chargebackFk)}/analyze/`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              'X-Tenant-ID': tenantIntegration,
            },
            body: JSON.stringify({
              action: 'analyze',
              chargeback_id: chargebackFk,
              payload: {
                tenant_id: tenantIntegration || tenantId,
                s3_key: s3Key,
                suggested_documents: suggestedSnapshot,
                reason_code: String(caseData.REASON_CODE ?? ''),
              },
            }),
          }
        );
        const analyzeData = await analyzeResp.json().catch(() => ({}));
        if (!analyzeResp.ok) {
          console.warn('Analyze failed:', analyzeData);
          return emptyAnalyzeResult(uploadItemType);
        }
        // Lambda validation failures used to come back as HTTP 200 with parseable:false
        // and raw_response.statusCode 422 — treat those as analyze failures.
        if (analyzeData?.raw_response?.statusCode >= 400) {
          console.warn('Analyze Lambda rejected payload:', analyzeData);
          return emptyAnalyzeResult(uploadItemType);
        }
        const findingCount = Number(analyzeData.pan_finding_count);
        return {
          parseable: Boolean(analyzeData.parseable),
          document_type: analyzeData.document_type || null,
          summary: analyzeData.summary || null,
          matched_suggestion_key:
            analyzeData.matched_suggestion_key != null
              ? String(analyzeData.matched_suggestion_key)
              : uploadItemType || null,
          match_rationale: analyzeData.match_rationale || null,
          contains_unmasked_pan: analyzeData.contains_unmasked_pan === true,
          pan_detection_method: analyzeData.pan_detection_method || null,
          pan_finding_count: Number.isFinite(findingCount) ? findingCount : null,
        };
      } catch (analyzeError) {
        console.warn('Analyze request error:', analyzeError);
        return emptyAnalyzeResult(uploadItemType);
      }
    };

    const uploadPromises = selectedFiles.map(async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      
      if (uploadNote) {
        formData.append('note', uploadNote);
      }

      try {
        const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${baseUrl}conductor/files/upload/${tenantId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
        }

        const result = await response.json();
        
        try {
          await AuditLoggers.chargebackUploadDocument(
            caseData?.CASE_NO || 'N/A',
            caseId,
            [file.name],
            {
              userId: localStorage.getItem('username') || 'unknown',
              tenantIntegration,
              caseStatus: caseData?.status || 'UNKNOWN',
              note: uploadNote || '',
              fileLocation: result.fileUrl || '',
              fileName: result.fileName || file.name,
              uploadTimestamp: new Date().toISOString()
            }
          );
        } catch (logError) {
          console.error('Failed to log document upload details:', logError);
        }
        
        return { file, result };
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
        throw error;
      }
    });

    try {
      setAnalyzingUpload(true);
      const results = await Promise.all(uploadPromises);
      console.log('All uploads successful:', results);
      
      try {
        const prepared = [];
        for (const { file, result } of results) {
          const analyze = await analyzeUploadedFile(file, result);
          const s3Key =
            result?.s3Key ||
            extractS3ObjectKeyFromUrl(result?.fileUrl || '') ||
            '';
          prepared.push({ file, result, analyze, s3Key });
        }

        const flagged = prepared.filter((item) => isConfirmedUnmaskedPan(item.analyze));
        const clear = prepared.filter((item) => !isConfirmedUnmaskedPan(item.analyze));
        const historyContext = {
          chargebackFk,
          statusValue,
          uploadNote,
          uploadItemType,
          suggestedSnapshot,
          tenantIntegration,
          token,
        };

        const historyResponses = await Promise.all(
          clear.map((item) =>
            postChargebackHistory(
              tenantIntegration,
              token,
              documentUploadedHistoryBody({ ...historyContext, ...item })
            )
          )
        );

        for (const item of clear) {
          const fileName = item.result?.fileName || item.file?.name || 'A file';
          await postChargebackHistory(tenantIntegration, token, {
            chargeback_id_fk: chargebackFk,
            action: 'ANALYSIS_FINISHED',
            message: `Letter service finished checking ${fileName}.`,
            letter_html: null,
            file_location: '',
            file_name: fileName,
            item_type: null,
            status: statusValue,
            created_by: localStorage.getItem('username') || 'system',
          });
        }

        const aiSummaries = clear
          .map((item) =>
            item.analyze?.parseable && item.analyze?.summary
              ? String(item.analyze.summary).trim()
              : ''
          )
          .filter(Boolean);
        if (aiSummaries.length) {
          setUploadNote(aiSummaries.join('\n\n'));
        }

        if (flagged.length) {
          setPanReview({ ...historyContext, items: flagged });
        }

        const clearFailed = clear.length > 0 && !historyResponses.every((r) => r.ok);
        if (clearFailed) {
          console.error('Failed to update chargeback history for one or more files');
          showToast('Files uploaded, but saving analysis/history failed for some files.', 'warning');
        } else if (flagged.length && clear.length) {
          await fetchCaseHistory();
          showToast(
            'Some files were saved. Others may contain a full card number and need a decision.',
            'warning'
          );
        } else if (!flagged.length) {
          await fetchCaseHistory();
          showToast(
            aiSummaries.length
              ? 'Documents uploaded. AI summary added to Notes.'
              : 'Documents uploaded and analyzed.',
            'success'
          );
        }

        // Convert after upload/analyze without locking the upload dialog.
        if (!clearFailed && clear.length) {
          convertClearDocuments({
            clearItems: clear,
            chargebackFk,
            statusValue,
            tenantIntegration,
            token,
          });
        }

        return { ok: !clearFailed, notePopulated: aiSummaries.length > 0, pendingPan: flagged.length > 0 };
      } catch (historyError) {
        console.error('Error updating chargeback history:', historyError);
        showToast('Files uploaded, but history update failed.', 'error');
        return { ok: false, notePopulated: false };
      }
    } catch (error) {
      console.error('Upload failed:', error);
      showToast(error.message || 'Upload failed. Please try again.', 'error');
      throw error;
      } finally {
      setAnalyzingUpload(false);
    }
  };

  const handleRemovePanFiles = async () => {
    if (!panReview || panReviewBusy) return;
    setPanReviewBusy(true);
    try {
      for (const item of panReview.items) {
        const fileName = item.result?.fileName || item.file?.name || 'A file';
        if (item.s3Key) {
          try {
            await deleteStoredFile(panReview.token, item.s3Key);
          } catch (error) {
            console.error('Failed to delete flagged file from storage:', error);
            showToast(`Could not delete ${fileName} from storage. It was not kept on the case.`, 'error');
          }
        }
        await postChargebackHistory(panReview.tenantIntegration, panReview.token, {
          chargeback_id_fk: panReview.chargebackFk,
          action: 'DOCUMENT_REMOVED',
          message: `${fileName} was not kept because it may contain an unredacted card number.`,
          letter_html: null,
          file_location: null,
          file_name: fileName,
          item_type: null,
          status: panReview.statusValue,
          created_by: localStorage.getItem('username') || 'system',
        });
      }
      await fetchCaseHistory();
      showToast('Those files were removed. Black out the card number and upload them again.', 'warning');
      setPanReview(null);
    } catch (error) {
      console.error('Failed to discard flagged files:', error);
      showToast(error.message || 'Could not finish removing the files.', 'error');
    } finally {
      setPanReviewBusy(false);
    }
  };

  const handleConfirmRemoveStoredFile = async () => {
    if (!fileToRemove || removingStoredFile) return;
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    const tenantIntegration = localStorage.getItem('tenant_integration');
    const chargebackFk = String(
      decodedCaseId || caseData.ID || caseData.id || caseData.caseNumber || ''
    );
    setRemovingStoredFile(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/chargebacks/${tenantIntegration}/history/${fileToRemove.id}?chargeback_id=${encodeURIComponent(chargebackFk)}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Tenant-ID': tenantIntegration,
          },
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.detail || 'Failed to remove the file from this case');
      }
      const s3Key = extractS3ObjectKeyFromUrl(fileToRemove.file?.url || fileToRemove.fileLocation || '');
      if (s3Key) {
        try {
          await deleteStoredFile(token, s3Key);
        } catch (error) {
          console.error('History row removed but storage delete failed:', error);
          showToast('The file was removed from the case, but deleting the stored copy failed.', 'warning');
        }
      }
      const statusSource =
        caseData.CHARGEBACK_STATUS ||
        caseData.Status ||
        (history && history.length > 0 ? history[0].rawStatus : null) ||
        'PENDING';
      await postChargebackHistory(tenantIntegration, token, {
        chargeback_id_fk: chargebackFk,
        action: 'DOCUMENT_REMOVED',
        message: `${fileToRemove.file?.name || 'A file'} was removed from case evidence.`,
        letter_html: null,
        file_location: null,
        file_name: fileToRemove.file?.name || '',
        item_type: null,
        status: normalizeHistoryStatusForDb(statusSource),
        created_by: localStorage.getItem('username') || 'system',
      });
      await fetchCaseHistory();
      showToast('File removed from this case.', 'success');
      setFileToRemove(null);
    } catch (error) {
      console.error('Failed to remove stored file:', error);
      showToast(error.message || 'Failed to remove the file.', 'error');
    } finally {
      setRemovingStoredFile(false);
    }
  };

  const handleStatusUpdate = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const tenantIntegration = localStorage.getItem('tenant_integration');
      const userEmail = localStorage.getItem('username') ;
      
      if (!token || !tenantIntegration) {
        throw new Error('Authentication or tenant information missing');
      }

      if (!status) {
        throw new Error('Please select a status');
      }

      // Get chargeback ID from URL
      const chargebackId = caseId || (caseData && (caseData.chargeback_id || caseData.id || caseData.CHARGEBACK_ID || caseData.MERCHANT_ID));
      
      if (!chargebackId) {
        throw new Error('Unable to determine chargeback ID');
      }

      const payload = {
        chargeback_id_fk: chargebackId,
        action: 'STATUS_UPDATE',
        status: status.toUpperCase().replace(/\s+/g, '_'),
        message: statusNote || `Chargeback status updated to '${status}'`,
        created_by: userEmail,
        file_location: '',
        file_name: ''
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}fastapi/v1/chargebacks/${tenantIntegration}/history/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-ID': tenantIntegration
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to update status: ${response.statusText}`);
      }

      // Refresh the history after successful update
      await fetchCaseHistory();
      
      // Update local state
      const newHistory = [
        ...(caseData.history || []),
        {
          id: Date.now().toString(),
          date: new Date().toISOString(),
          status: status,
          action: 'STATUS_UPDATE',
          message: statusNote || `Status updated to ${status}`,
          user: userEmail
        }
      ];
      
      setCaseData(prevData => ({
        ...prevData,
        history: newHistory,
        status: status
      }));
      
      setShowStatusDialog(false);
      setStatus('');
      setStatusNote('');
      
      // Show success toast
      showToast('Status updated successfully!', 'success');

    } catch (error) {
      console.error('Error updating status:', error);
      showToast(error.message || 'Failed to update status. Please try again.', 'error');
    }
  };

  const handleAddNote = async () => {
    if (!canAdd) return;
    try {
      if (!note.trim()) {
        throw new Error('Please enter a note');
      }

      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const userEmail = localStorage.getItem('username') || 'unknown';
      const tenantIntegration = localStorage.getItem('tenant_integration');

      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      // Log the note addition
      try {
        await AuditLoggers.chargebackAddNote(
          caseData?.CASE_NO || 'N/A',
          caseId,
          note,
          {
            userId: userEmail,
            tenantIntegration,
            caseStatus: caseData?.status || 'UNKNOWN',
            timestamp: new Date().toISOString()
          }
        );
      } catch (logError) {
        console.error('Failed to log note addition:', logError);
        // Don't fail the operation if logging fails
      }

      // Get chargeback ID from URL
      const chargebackId = caseId || (caseData && (caseData.chargeback_id || caseData.id || caseData.CHARGEBACK_ID || caseData.MERCHANT_ID));
      
      if (!chargebackId) {
        throw new Error('Unable to determine chargeback ID');
      }

      const payload = {
        chargeback_id_fk: chargebackId,
        action: 'NOTE_ADDED',
        status: (caseData.status || 'In Progress').toUpperCase().replace(/\s+/g, '_'),
        message: note,
        created_by: userEmail,
        file_location: '',
        file_name: ''
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}fastapi/v1/chargebacks/${tenantIntegration}/history/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-ID': tenantIntegration
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to add note: ${response.statusText}`);
      }

      // Refresh the history after successful update
      await fetchCaseHistory();
      
      // Update local state
      const newHistory = [
        ...(caseData.history || []),
        {
          id: Date.now().toString(),
          date: new Date().toISOString(),
          status: caseData.status || 'In Progress',
          action: 'NOTE_ADDED',
          message: note,
          user: userEmail
        }
      ];
      
      setCaseData(prevData => ({
        ...prevData,
        history: newHistory
      }));
      
      setShowNoteDialog(false);
      setNote('');
      
      // Show success toast
      showToast('Note added successfully!', 'success');

    } catch (error) {
      console.error('Error adding note:', error);
      showToast(error.message || 'Failed to add note. Please try again.', 'error');
    }
  };

  const handleFightTransaction = async () => {
    if (!canEdit) return;
    if (classifyChargebackStage(caseData) === CHARGEBACK_STAGE.EXPIRED) {
      showToast('This chargeback is expired. The respond-by date has passed.', 'warning');
      return;
    }
    try {
      // Check if the last history entry was already a Fight Transaction
      if (history && history.length > 0) {
        const lastHistoryEntry = history[0]; // History is sorted with latest first
        if (lastHistoryEntry.message === 'Fight Transaction Initiated' ||
            (lastHistoryEntry.action === 'ACTION_UPDATE' && lastHistoryEntry.message?.includes('Fight Transaction'))) {
          showToast('Fight Transaction has already been initiated for this case. Please check the case history.', 'warning');
          return;
        }
      }

      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const tenantIntegration = localStorage.getItem('tenant_integration');
      const userEmail = localStorage.getItem('username') || 'unknown';
      
      // Log the fight transaction action
      try {
        await AuditLoggers.chargebackFightTransaction(
          caseData.CASE_NO || 'N/A',
          caseId,
          {
            userId: userEmail,
            tenantIntegration,
            caseStatus: caseData.status || 'UNKNOWN',
            amount: caseData.amount,
            currency: caseData.currency
          }
        );
      } catch (logError) {
        console.error('Failed to log fight transaction:', logError);
        // Don't fail the operation if logging fails
      }

      if (!token || !tenantIntegration) {
        throw new Error('Authentication or tenant information missing');
      }

      // Get chargeback ID from URL
      const chargebackId = caseId || (caseData && (caseData.chargeback_id || caseData.id || caseData.CHARGEBACK_ID || caseData.MERCHANT_ID));
      
      if (!chargebackId) {
        throw new Error('Unable to determine chargeback ID');
      }

      // Extract numeric ID from record_id if it has 'row-' prefix, otherwise use the value as is
      // Ensure the result is always a positive integer (>= 1)
      const parseRecordId = (id) => {
        if (!id) return 1; // Default to 1 instead of 0
        
        let num;
        if (typeof id === 'number') {
          num = Math.floor(id);
        } else if (typeof id === 'string') {
          const cleanId = id.startsWith('row-') ? id.replace('row-', '') : id;
          num = parseInt(cleanId, 10);
        }
        
        // Ensure the number is at least 1
        return Math.max(1, isNaN(num) ? 1 : num);
      };

      const actionPayload = {
        processor: String(caseData.PROCESSOR || caseData.processor || caseData.CARD_BRAND || 'UNKNOWN'),
        record_id: parseRecordId(caseData.id || caseData.record_id || 0),
        case_number: caseData.CASE_NO || `CASE-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`,
        action_type: 'FIGHT_TRANSACTION',
        action_date: new Date().toISOString(),
        user_id: userEmail,
        notes_on_fight_transaction: 'Fight transaction initiated',
        created_by: userEmail
      };

      // Call the actions API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}fastapi/v1/chargebacks/${tenantIntegration}/actions/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-ID': tenantIntegration
        },
        body: JSON.stringify(actionPayload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to process fight transaction: ${response.statusText}`);
      }

      // Update history with fight transaction initiation
      const historyPayload = {
        chargeback_id_fk: chargebackId,
        action: 'ACTION_UPDATE',
        status: caseData.status || 'PENDING', // Keep current status
        message: 'Fight Transaction Initiated',
        created_by: userEmail,
        file_location: '',
        file_name: ''
      };

      const historyResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}fastapi/v1/chargebacks/${tenantIntegration}/history/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-ID': tenantIntegration
        },
        body: JSON.stringify(historyPayload)
      });

      if (!historyResponse.ok) {
        const errorData = await historyResponse.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to update history: ${historyResponse.statusText}`);
      }

      // Refresh the case data and history
      await fetchCaseHistory();
      const newStatus = true;
      setIsFightTransactionCompleted(newStatus);
      // Save to localStorage to persist across page refreshes
      localStorage.setItem(`fightCompleted_${caseId}`, newStatus.toString());
      showToast('Fight transaction processed successfully!', 'success');

    } catch (error) {
      console.error('Error processing fight transaction:', error);
      showToast(error.message || 'Failed to process fight transaction. Please try again.', 'error');
    }
  };

  const handleGeneratePdf = () => {
    if (!canExport) return;
    try {
      if (!caseData) {
        throw new Error('No case data available to generate PDF');
      }

      const chargebackId = caseData.ID != null ? String(caseData.ID) : '';

      if (!chargebackId) {
        throw new Error('Could not find a valid chargeback ID');
      }

      console.log('Navigating to dispute letter with chargeback ID:', chargebackId);

      navigate('/chargeback/dispute-letter', { 
        state: { 
          caseData: caseData,
          chargebackId,
          selectedProperty: location.state?.selectedProperty || null,
        } 
      });
    } catch (error) {
      console.error('Error navigating to dispute letter:', error);
      showToast(error.message || 'Failed to open dispute letter', 'error');
    }
  };

  const getStatusChip = (status) => {
    if (!status) return <Chip label="Unknown" size="small" />;
    
    switch (status.toLowerCase()) {
      case 'won':
      case 'win':
        return <Chip label={status} color="success" size="small" />;
      case 'lost':
      case 'loss':
        return <Chip label={status} color="error" size="small" />;
      case 'in progress':
      case 'pending':
        return <Chip label={status} color="warning" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  // Authoritative "has this case ever been fought?" flag. Checks the whole
  // history (not just the newest row, which would flip back to "not fought"
  // the moment a note is added). Falls back to the legacy local-storage flag
  // for the brief window before the history API responds on first load.
  const isFightAlreadyInitiated =
    (Array.isArray(history) && history.some((h) =>
      h?.message === 'Fight Transaction Initiated' ||
      (h?.action === 'ACTION_UPDATE' && h?.message?.includes('Fight Transaction'))
    )) || isFightTransactionCompleted;

  const isResponseExpired = classifyChargebackStage(caseData) === CHARGEBACK_STAGE.EXPIRED;

  const fulfilledSuggestionKeys = getFulfilledSuggestionKeys(history);
  const responsePosture = reasonCodeResolved?.response_posture;
  const showDocumentChecklist = shouldShowDocumentChecklist(responsePosture);
  const refundProofOnly = isRefundProofOnly(responsePosture);
  const nonActionablePosture = isNonActionablePosture(responsePosture);
  const notADispute = isNotADisputePosture(responsePosture);
  const acceptLiability = isAcceptLiabilityPosture(responsePosture);
  const complianceCase = isComplianceCategory(reasonCodeResolved?.harmonate_category);
  const displayReasonCode =
    reasonCodeResolved?.resolved_reason_code || caseData.REASON_CODE || 'N/A';
  const displayReason =
    reasonCodeResolved?.resolved_reason || caseData.REASON || 'N/A';
  const displayCategory = formatCategoryLabel(
    reasonCodeResolved?.harmonate_category,
    reasonCodeResolved?.harmonate_subcategory
  );
  const displayCaseType =
    reasonCodeResolved?.case_type || caseData.CASE_TYPE || caseData.case_type || '';
  const dueDateDisplay = formatDueDateDisplay(caseData, formatDate);
  const winLossLabel = formatProvisionalWinLabel(
    caseData.CHARGEBACK_WIN_LOSS || caseData.chargeback_win_loss,
    caseData.PROVISIONAL_WIN_UNTIL || caseData.provisional_win_until,
    formatDate
  );
  const preArbCaseNo =
    caseData.PRE_ARBITRATION_CASE_NO || caseData.pre_arbitration_case_no || '';
  const originalCaseNo = caseData.ORIGINAL_CASE_NO || caseData.original_case_no || '';
  const suggestedDocEntries = showDocumentChecklist
    ? sortSuggestedDocEntries(
        refundProofOnly
          ? Object.fromEntries(
              Object.entries(suggestedDocumentsMap || {}).filter(([, label]) =>
                /refund/i.test(String(label || ''))
              )
            )
          : suggestedDocumentsMap
      )
    : [];
  // If refund-proof posture has no refund-labelled docs, show the full map as fallback.
  const effectiveSuggestedDocEntries =
    refundProofOnly && suggestedDocEntries.length === 0 && showDocumentChecklist
      ? sortSuggestedDocEntries(suggestedDocumentsMap)
      : suggestedDocEntries;

  return (
    <Box
      sx={{
        ...harmonatePageSx,
        height: '100%',
        maxHeight: '100%',
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        display: 'block',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={harmonateUploadOutlinedSx}
        >
          Back to List
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Case Information */}
        <Grid item xs={12} md={6}>
          <Card sx={{ ...harmonateCardSx, bgcolor: ht.white, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardHeader
              title="Case Information"
              titleTypographyProps={{ variant: 'h6', fontWeight: 'bold', sx: { color: ht.text } }}
              sx={{ pb: 1 }}
            />
            <CardContent sx={{ pb: 2 }}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <List dense disablePadding>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemText
                        primary="Case Number"
                        secondary={caseData.CASE_NO}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemText
                        primary="Merchant ID"
                        secondary={caseData.MERCHANT_ID}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemText
                        primary="Merchant Name"
                        secondary={caseData.MERCHANT_NAME}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemText
                        primary="Cardholder Number"
                        secondary={caseData.CARDHOLDER_NO}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemText
                        primary="Card Brand"
                        secondary={caseData.CARD_BRAND}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <List dense disablePadding>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemText
                        primary="Transaction Amount"
                        secondary={formatAmount(caseData.TRANSACTION_AMOUNT || '0')}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemText
                        primary="Chargeback Amount"
                        secondary={formatAmount(caseData.CHARGEBACK_AMOUNT || '0')}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemText
                        primary="Transaction Date"
                        secondary={caseData.TRANSACTION_DATE ? formatDate(new Date(caseData.TRANSACTION_DATE)) : 'N/A'}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemText
                        primary="Date Received"
                        secondary={caseData.DATE_RECEIVED ? formatDate(new Date(caseData.DATE_RECEIVED)) : 'N/A'}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemText
                        primary="Reason Code"
                        secondary={
                          loadingReasonCodeResolved && !reasonCodeResolved?.found
                            ? 'Loading...'
                            : displayReasonCode
                        }
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemText
                        primary="Due Date"
                        secondary={dueDateDisplay.text}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{
                          variant: 'body2',
                          title: dueDateDisplay.confidence
                            ? `Estimate confidence: ${dueDateDisplay.confidence}`
                            : undefined,
                        }}
                      />
                    </ListItem>
                  </List>
                </Grid>
                
                {/* Additional Details */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                    {notADispute ? 'Case Details' : 'Dispute Details'}
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <List dense disablePadding>
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <ListItemText
                            primary="Reason"
                            secondary={displayReason}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                            secondaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                        {displayCategory ? (
                          <ListItem sx={{ px: 0, py: 0.5 }}>
                            <ListItemText
                              primary="Category"
                              secondary={displayCategory}
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                              secondaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ) : null}
                        {displayCaseType ? (
                          <ListItem sx={{ px: 0, py: 0.5 }}>
                            <ListItemText
                              primary="Type"
                              secondary={displayCaseType}
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                              secondaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ) : null}
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <ListItemText
                            primary="Response Status"
                            secondary={caseData.CHARGEBACK_RESPONDED}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                            secondaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                        {winLossLabel ? (
                          <ListItem sx={{ px: 0, py: 0.5 }}>
                            <ListItemText
                              primary="Win / Loss"
                              secondary={winLossLabel}
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                              secondaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ) : null}
                      </List>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <List dense disablePadding>
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <ListItemText
                            primary="Card Type"
                            secondary={caseData.CARD_TYPE}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                            secondaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <ListItemText
                            primary="Card Issuer"
                            secondary={caseData.CARD_ISSUER}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                            secondaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <ListItemText
                            primary="Processor"
                            secondary={caseData.PROCESSOR}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                            secondaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>
                </Grid>

                {(reasonCodeResolved?.posture_note ||
                  acceptLiability ||
                  notADispute ||
                  complianceCase ||
                  preArbCaseNo ||
                  originalCaseNo) && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    {(reasonCodeResolved?.posture_note ||
                      acceptLiability ||
                      notADispute ||
                      complianceCase) && (
                      <>
                        <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                          {acceptLiability
                            ? 'Instructions (cannot be won)'
                            : notADispute
                              ? 'Instructions (not a dispute)'
                              : complianceCase
                                ? 'Instructions (Compliance & Regulatory — urgent)'
                                : 'Instructions'}
                        </Typography>
                        <Alert
                          severity={
                            acceptLiability
                              ? 'error'
                              : notADispute
                                ? 'info'
                                : complianceCase
                                  ? 'warning'
                                  : 'info'
                          }
                          sx={{ alignItems: 'flex-start' }}
                        >
                          {reasonCodeResolved?.posture_note ||
                            (acceptLiability
                              ? 'This case cannot be won. Accept liability.'
                              : notADispute
                                ? 'This is not a dispute. Nothing is at stake.'
                                : 'Follow the guidance for this reason code.')}
                        </Alert>
                      </>
                    )}
                    {preArbCaseNo ? (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Issuer pre-arbitration received → case{' '}
                        <MuiLink
                          component="button"
                          type="button"
                          onClick={() =>
                            navigate(`/chargeback/case/${encodeURIComponent(String(preArbCaseNo))}`)
                          }
                          sx={harmonateLinkSx}
                        >
                          {String(preArbCaseNo)}
                        </MuiLink>
                      </Typography>
                    ) : null}
                    {originalCaseNo ? (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Pre-arbitration on case{' '}
                        <MuiLink
                          component="button"
                          type="button"
                          onClick={() =>
                            navigate(`/chargeback/case/${encodeURIComponent(String(originalCaseNo))}`)
                          }
                          sx={harmonateLinkSx}
                        >
                          {String(originalCaseNo)}
                        </MuiLink>
                      </Typography>
                    ) : null}
                  </Grid>
                )}

                {/* Suggested Documents — only when RESPONSE_POSTURE allows. */}
                {showDocumentChecklist ? (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                    {refundProofOnly ? 'Suggested Documents (refund proof only)' : 'Suggested Documents'}
                  </Typography>
                  {loadingSuggestedDocuments ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                      <CircularProgress size={18} />
                      <Typography variant="body2" color="text.secondary">
                        Loading recommendations...
                      </Typography>
                    </Box>
                  ) : effectiveSuggestedDocEntries.length > 0 ? (
                    <List dense disablePadding>
                      {effectiveSuggestedDocEntries.map(([key, doc]) => {
                        const fulfilled = fulfilledSuggestionKeys.has(String(key));
                        const matchItem = (history || []).find((h) => {
                          const action = String(h?.action || '').toUpperCase();
                          if (action !== 'DOCUMENT_UPLOADED' && action !== 'FILE_UPLOAD') return false;
                          const matched =
                            h?.analyzeMeta?.matched_suggestion_key != null
                              ? String(h.analyzeMeta.matched_suggestion_key)
                              : String(h?.itemType || '').trim();
                          return matched === String(key);
                        });
                        const rationale = matchItem?.analyzeMeta?.match_rationale;
                        return (
                          <ListItem
                            key={key}
                            sx={{ px: 0, py: 0.75, alignItems: 'flex-start', display: 'block' }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              {fulfilled ? (
                                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main', mt: 0.25 }} />
                              ) : (
                                <CancelIcon fontSize="small" sx={{ color: 'error.main', mt: 0.25 }} />
                              )}
                              <Box>
                                <Typography variant="body2">
                                  <Box component="span" sx={{ fontWeight: 500, mr: 0.5 }}>
                                    {key}.
                                  </Box>
                                  {doc}
                                </Typography>
                                {fulfilled && rationale && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                    {rationale}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </ListItem>
                        );
                      })}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {refundProofOnly
                        ? 'Ask for refund proof for this case.'
                        : 'No document recommendations are available for this reason code.'}
                    </Typography>
                  )}
                </Grid>
                ) : null}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Case Status & History */}
        <Grid item xs={12} md={6}>
          <Card sx={{ ...harmonateCardSx, bgcolor: ht.white, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <CardHeader 
              title="Case Status & History"
              titleTypographyProps={{ sx: { color: ht.text, fontWeight: 700 } }}
              sx={{ pb: 1 }}
            />
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                px: 2,
                pb: 1.5,
              }}
            >
              <Button 
                variant="contained" 
                size="medium"
                onClick={() => setShowNoteDialog(true)}
                startIcon={<NoteAddIcon />}
                disabled={!canAdd}
                sx={harmonateUploadOutlinedSx}
              >
                Add Note
              </Button>
              {!isResponseExpired && !nonActionablePosture && (
              <Tooltip
                title={
                  convertingDocuments
                    ? 'Wait until document conversion finishes'
                    : isFightAlreadyInitiated
                      ? 'Fight Transaction has already been initiated'
                      : !canEdit
                        ? 'You do not have permission to fight this case'
                        : 'Start Fight Transaction'
                }
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    size="medium"
                    onClick={handleFightTransaction}
                    startIcon={<DescriptionIcon />}
                    disabled={isFightAlreadyInitiated || !canEdit || convertingDocuments}
                    sx={harmonateProceedButtonSx}
                  >
                    Fight Transaction
                  </Button>
                </span>
              </Tooltip>
              )}
              {!nonActionablePosture && (
              <Button 
                variant="contained" 
                size="medium"
                onClick={handleGeneratePdf}
                disabled={!isFightAlreadyInitiated || !canExport}
                startIcon={<DownloadIcon />}
                sx={harmonateBlueButtonSx}
                title={!isFightAlreadyInitiated ? "Complete Fight Transaction first" : ""}
              >
                Generate Filing PDF
              </Button>
              )}
              <Button
                variant="contained"
                size="medium"
                disabled
                startIcon={<CheckCircleIcon />}
                sx={harmonateBlueButtonSx}
                title="Coming soon"
              >
                Approve
              </Button>
              {!nonActionablePosture && (
              <Button
                variant="contained"
                size="medium"
                onClick={() => setShowUploadDialog(true)}
                startIcon={<UploadIcon />}
                disabled={!canUpload}
                sx={harmonateUploadOutlinedSx}
              >
                Upload Supporting Documents
              </Button>
              )}
            </Box>
            <CardContent sx={{ pt: 1, overflowX: 'auto' }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  CURRENT STATUS
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Chip 
                    label={caseData.CHARGEBACK_STATUS || 'Unknown'}
                    size="small"
                    sx={{
                      bgcolor: ht.secondaryTab,
                      color: ht.text,
                      border: `1px solid ${ht.line}`,
                      fontWeight: 500,
                    }}
                  />
                </Box>
                
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  HISTORY
                </Typography>
                
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" gutterBottom sx={harmonateSectionTitleSx}>Case Status</Typography>
                  <Paper sx={{ ...harmonateCardSx, bgcolor: ht.white, p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2">Status</Typography>
                        <Chip 
                          label={formatStatus(caseData.status)} 
                          sx={{
                            mt: 1,
                            textTransform: 'capitalize',
                            bgcolor: ht.secondaryTab,
                            color: ht.text,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2">Last Updated</Typography>
                        <Typography>{caseData.lastUpdated ? formatDateTime(new Date(caseData.lastUpdated)) : 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2">Updated By</Typography>
                        <Typography>{caseData.updatedBy || 'N/A'}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={harmonateSectionTitleSx}>Case History</Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={downloadingAll ? <CircularProgress size={16} /> : <DownloadIcon />}
                      onClick={handleDownloadAllFiles}
                      disabled={downloadingAll || history.filter(item => item.file && item.file.url).length === 0 || !canExport}
                      sx={harmonateUploadOutlinedSx}
                    >
                      {downloadingAll ? 'Downloading...' : 'Download All'}
                    </Button>
                  </Box>
                  {loadingHistory ? (
                    <Box display="flex" justifyContent="center" p={3}>
                      <CircularProgress />
                    </Box>
                  ) : historyError ? (
                    <Alert severity="error">{historyError}</Alert>
                  ) : history.filter((item) => isVisibleHistoryAction(item.action)).length === 0 ? (
                    <Alert severity="info">No history available for this case.</Alert>
                  ) : (
                    <Paper sx={{ ...harmonateCardSx, bgcolor: ht.white }}>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ bgcolor: ht.white }}>
                              <TableCell sx={{ minWidth: '180px', fontWeight: 700, color: ht.text, borderBottom: `1px solid ${ht.line}` }}>Date</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: ht.text, borderBottom: `1px solid ${ht.line}` }}>Action</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: ht.text, borderBottom: `1px solid ${ht.line}` }}>Status</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: ht.text, borderBottom: `1px solid ${ht.line}` }}>Details</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: ht.text, borderBottom: `1px solid ${ht.line}` }}>User</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: ht.text, borderBottom: `1px solid ${ht.line}` }}>File</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {history
                              .filter((item) => isVisibleHistoryAction(item.action))
                              .map((item) => (
                              <TableRow key={item.id} sx={{ '&:hover': { bgcolor: ht.secondaryTab } }}>
                                <TableCell sx={{ minWidth: '180px', borderBottom: `1px solid ${ht.line}` }}>{item.date}</TableCell>
                                <TableCell sx={{ borderBottom: `1px solid ${ht.line}` }}>{item.action}</TableCell>
                                <TableCell sx={{ borderBottom: `1px solid ${ht.line}` }}>
                                  <Chip 
                                    label={item.status} 
                                    size="small"
                                    sx={{
                                      bgcolor: item.status === 'COMPLETED' ? ht.secondaryTab : ht.listBg,
                                      color: ht.text,
                                    }}
                                  />
                                </TableCell>
                                <TableCell sx={{ width: 72, whiteSpace: 'nowrap', borderBottom: `1px solid ${ht.line}` }}>
                                  <IconButton
                                    size="small"
                                    title="View summary"
                                    aria-label="View summary"
                                    onClick={() =>
                                      setHistoryDetailsDialog({
                                        open: true,
                                        title: 'Summary',
                                        message: getHistorySummaryDisplayText(item.message),
                                      })
                                    }
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                                <TableCell sx={{ borderBottom: `1px solid ${ht.line}` }}>{item.user}</TableCell>
                                <TableCell sx={{ borderBottom: `1px solid ${ht.line}` }}>
                                  {item.file ? (
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                      <Box>
                                        <MuiLink
                                          href="#"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            handleFileDownload(item.file.url, item.file.name);
                                          }}
                                          sx={harmonateLinkSx}
                                        >
                                          {item.file.name}
                                        </MuiLink>
                                        {item.panRiskAccepted && (
                                          <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                                            Risk accepted
                                          </Typography>
                                        )}
                                      </Box>
                                      {['DOCUMENT_UPLOADED', 'FILE_UPLOAD'].includes(String(item.action || '').toUpperCase()) && (
                                        <IconButton
                                          size="small"
                                          title="Remove file"
                                          aria-label="Remove file"
                                          disabled={!canUpload && !canEdit}
                                          onClick={() => setFileToRemove(item)}
                                        >
                                          <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                    </Box>
                                  ) : 'N/A'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 
        // Commenting out Supporting Documents section as per requirements
        // Right Column - Supporting Documents 
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader 
              title="Supporting Documents"
              sx={{ pb: 1 }}
            />
            <CardContent sx={{ pt: 1 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  {formatDate(new Date())}: Case Documentation
                  <IconButton size="small">
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Typography>
              </Box>
              
              <Button 
                variant="outlined" 
                startIcon={<UploadIcon />}
                fullWidth
                onClick={() => setShowUploadDialog(true)}
                sx={{ mt: 2, py: 1.5 }}
              >
                Upload Documents
              </Button>
            </CardContent>
          </Card>
        </Grid>
        */}
      </Grid>

      {/* Status Update Dialog */}
      <Dialog open={showStatusDialog} onClose={() => setShowStatusDialog(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Update Case Status</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <TextField
            select
            fullWidth
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            InputLabelProps={harmonateInputLabelProps}
            sx={harmonateTextFieldSx}
          >
            <MenuItem value="PENDING">PENDING</MenuItem>
            <MenuItem value="WON">WON</MenuItem>
            <MenuItem value="LOST">LOST</MenuItem>
          </TextField>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Notes"
            value={statusNote}
            onChange={(e) => setStatusNote(e.target.value)}
            placeholder="Add any relevant notes about this status update"
            InputLabelProps={harmonateInputLabelProps}
            sx={harmonateTextFieldSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, borderTop: `1px solid ${ht.line}` }}>
          <Button onClick={() => setShowStatusDialog(false)} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button onClick={handleStatusUpdate} variant="contained" sx={harmonateProceedButtonSx}>
            Update Status
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Add Note Dialog */}
      <Dialog open={showNoteDialog} onClose={() => setShowNoteDialog(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Add Note</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <TextField
            fullWidth
            multiline
            rows={6}
            label="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Enter your note here..."
            InputLabelProps={harmonateInputLabelProps}
            sx={harmonateTextFieldSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, borderTop: `1px solid ${ht.line}` }}>
          <Button onClick={() => setShowNoteDialog(false)} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button onClick={handleAddNote} variant="contained" sx={harmonateProceedButtonSx}>
            Add Note
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Upload Documents Dialog */}
      <Dialog 
        open={showUploadDialog} 
        onClose={() => {
          setShowUploadDialog(false);
          setSelectedFiles([]);
          setUploadNote('');
          setUploadItemType('');
        }}
        maxWidth="md"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Upload Documents</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {PAN_UPLOAD_WARNING}
          </Alert>
          <Box sx={{ mb: 1 }}>
            <Box
              onClick={analyzingUpload ? undefined : handleChooseFile}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (analyzingUpload) return;
                addFiles(Array.from(e.dataTransfer.files || []));
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              sx={{
                border: `2px dashed ${ht.line}`,
                borderRadius: '20px',
                bgcolor: ht.listBg,
                p: 4,
                textAlign: 'center',
                cursor: analyzingUpload ? 'default' : 'pointer',
                mb: 2,
                '&:hover': {
                  bgcolor: analyzingUpload ? ht.listBg : ht.secondaryTab,
                  borderColor: ht.blue,
                },
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 48, color: ht.text, mb: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Drag your file(s) to start uploading
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                OR
              </Typography>
              <Button
                variant="contained"
                component="label"
                disabled={analyzingUpload}
                onClick={(e) => e.stopPropagation()}
                sx={harmonateUploadButtonSx}
              >
                Browse files
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  multiple
                  disabled={analyzingUpload}
                  onChange={handleFileChange}
                />
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                Select one or more files
              </Typography>
            </Box>
            {selectedFiles.length > 0 && (
              <Box sx={{ mt: 2, mb: 2, maxHeight: '200px', overflow: 'auto', border: `1px solid ${ht.line}`, borderRadius: ht.fieldRadius, p: 1 }}>
                {selectedFiles.map((file, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 1, bgcolor: ht.listBg, borderRadius: ht.fieldRadius }}>
                    <DescriptionIcon fontSize="small" />
                    <Typography variant="body2" sx={{ flexGrow: 1, wordBreak: 'break-word' }}>
                      {file.name} ({formatAmount(file.size / 1024, '#,##0.00')} KB)
                    </Typography>
                    <IconButton size="small" onClick={() => handleRemoveFile(index)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
            {Object.keys(suggestedDocumentsMap).length > 0 && (
              <FormControl fullWidth margin="normal">
                <InputLabel id="item-type-label">Manual match (optional)</InputLabel>
                <Select
                  labelId="item-type-label"
                  label="Manual match (optional)"
                  value={uploadItemType}
                  onChange={(e) => setUploadItemType(e.target.value)}
                  MenuProps={harmonateSelectMenuProps}
                >
                  <MenuItem value="">
                    <em>None — let AI match</em>
                  </MenuItem>
                  {sortSuggestedDocEntries(suggestedDocumentsMap).map(([key, item]) => (
                    <MenuItem key={key} value={key}>
                      {key}. {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              fullWidth
              label={needsManualUploadNote ? 'Describe this document (required)' : 'Notes'}
              value={uploadNote}
              onChange={(e) => setUploadNote(e.target.value)}
              multiline
              minRows={10}
              maxRows={18}
              required={needsManualUploadNote}
              helperText={
                needsManualUploadNote
                  ? 'This file type cannot be auto-analyzed (video/audio/archive or over 5 MB). Please describe what it is and how it supports the rebuttal.'
                  : 'After upload, the AI summary is saved to the case notes and this dialog closes.'
              }
              InputLabelProps={harmonateInputLabelProps}
              sx={{
                ...harmonateTextFieldSx,
                '& .MuiInputBase-root': {
                  alignItems: 'flex-start',
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, borderTop: `1px solid ${ht.line}` }}>
          <Button onClick={() => {
            setShowUploadDialog(false);
            setSelectedFiles([]);
            setUploadNote('');
            setUploadItemType('');
          }} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={async () => {
              try {
                const result = await handleFileUpload();
                if (!result?.ok) return;
                setShowUploadDialog(false);
                setSelectedFiles([]);
                setUploadNote('');
                setUploadItemType('');
              } catch (error) {
                console.error('Upload failed:', error);
              }
            }}
            disabled={
              selectedFiles.length === 0 ||
              analyzingUpload ||
              Boolean(panReview) ||
              (needsManualUploadNote && !String(uploadNote || '').trim())
            }
            sx={harmonateProceedButtonSx}
          >
            {analyzingUpload
              ? 'Uploading & Analyzing...'
              : 'Upload Documents'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Details Dialog */}
      <Dialog
        open={historyDetailsDialog.open}
        onClose={() => setHistoryDetailsDialog({ open: false, title: '', message: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Typography variant="h6" component="span">
            {historyDetailsDialog.title || 'Details'}
          </Typography>
          <IconButton
            size="small"
            aria-label="Close"
            onClick={() => setHistoryDetailsDialog({ open: false, title: '', message: '' })}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, wordBreak: 'break-word' }}
          >
            {historyDetailsDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            onClick={() => setHistoryDetailsDialog({ open: false, title: '', message: '' })}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog
        open={Boolean(panReview)}
        onClose={() => {
          if (!panReviewBusy) handleRemovePanFiles();
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Full card number found</DialogTitle>
        <DialogContent sx={harmonateDialogFormContentSx}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            These files contain a full, unredacted card number. They will not be kept. Black out the number and upload the file again.
          </Typography>
          <Box component="ul" sx={{ mt: 0, pl: 2.5 }}>
            {(panReview?.items || []).map((item, index) => (
              <li key={`${item.file?.name || 'file'}-${index}`}>
                <Typography variant="body2">{item.file?.name || item.result?.fileName || 'Uploaded file'}</Typography>
              </li>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, borderTop: `1px solid ${ht.line}` }}>
          <Button
            onClick={handleRemovePanFiles}
            variant="contained"
            disabled={panReviewBusy}
            sx={harmonateProceedButtonSx}
          >
            {panReviewBusy ? 'Removing...' : 'OK'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(fileToRemove)}
        onClose={() => {
          if (!removingStoredFile) setFileToRemove(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Remove file</DialogTitle>
        <DialogContent sx={harmonateDialogFormContentSx}>
          <Typography variant="body2">
            Remove {fileToRemove?.file?.name || 'this file'} from the case? It will not be included in the dispute letter.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, borderTop: `1px solid ${ht.line}` }}>
          <Button
            onClick={() => setFileToRemove(null)}
            variant="outlined"
            disabled={removingStoredFile}
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRemoveStoredFile}
            variant="contained"
            disabled={removingStoredFile}
            sx={harmonateProceedButtonSx}
          >
            {removingStoredFile ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {convertingDocuments && (
        <Box
          sx={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            zIndex: 1400,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 2,
            py: 1.25,
            borderRadius: 2,
            bgcolor: ht.paper || '#fff',
            border: `1px solid ${ht.line}`,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          }}
        >
          <CircularProgress size={18} />
          <Typography variant="body2" sx={{ color: ht.text }}>
            {convertingCount > 1
              ? `Documents being converted (${convertingCount})`
              : 'Documents being converted'}
          </Typography>
        </Box>
      )}

      {/* Toast Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseToast} 
          severity={toast.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CaseDetails;